import React, { JSX } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { Preview } from 'react-dnd-preview';
import EDDraggableChip, { IDraggableChipDragItem } from './EDDraggableChip';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import EDTooltip from '../../Tooltip/EDTooltip';
import EDDraggableChipView from './EDDraggableChipView';
import { useIsTouchDevice } from '../../../Components/BreakpointContext/BreakpointContextUtils';
import { propEq } from 'ramda';
import { namedError } from '../../../Utils/Logger';

export interface IItem {
  name: string;
  id: string | number;
  avatarHighLighted?: boolean;
  /** Alternativ ikon till avataren, default är drag */
  alternateAvatar?: IconType;
  avatarTooltip?: string;
  hide?: boolean;
  labelIcon?: React.ReactNode;
  deleteIcon?: JSX.Element;
  hoverAvatar?: JSX.Element;
}

interface IPreviewData {
  itemType: string;
  item: IDraggableChipDragItem;
  style: object;
}

interface IProps {
  items: IItem[];
  onItemsUpdate: (items: IItem[]) => void;
  canDelete?: boolean;
  chipSelection?: boolean;
  onChipSelectionUpdate?: (expandedLevel: number) => void;
  dragHandle?: boolean;
  clickDisabled?: boolean;
  onClick?: (item: IItem) => void;
  disabledReason?: string;
  showHidden?: boolean;
  onDelete?: (item: IItem) => void;
}

interface IInnerProps extends IProps {
  isTouchDevice: boolean;
}

/**
 * Lista med draggable chips
 */
class EDDraggableChipListBase extends React.PureComponent<IInnerProps> {
  constructor(props: IInnerProps) {
    super(props);

    this.state = {
      selectedLevel: 0,
    };
  }

  onDelete = (item: IItem) => () => {
    if (this.props.onDelete) {
      this.props.onDelete(item);
    } else {
      let items = [
        ...this.props.items.filter(x => {
          const xId = x.id != null ? x.id : x.name;
          const itemId = item.id != null ? item.id : item.name;
          return xId !== itemId;
        }),
      ];

      this.props.onItemsUpdate(items);
    }
  };

  moveItem = (fromId: string | number, toIndex: number) => {
    let list = this.props.items;
    const fromIndex = list.findIndex(propEq('id', fromId));
    if (fromIndex > -1) {
      let item = list[fromIndex];
      list.splice(fromIndex, 1);
      list.splice(toIndex, 0, item);
      this.props.onItemsUpdate(list);
    } else {
      throw namedError(
        'moveItemMissingId',
        `Saknat id "${fromId}" saknas i [${list
          .map(item => item.id)
          .join(', ')}]`
      );
    }
  };

  getNewIndex = (oldIndex: number, selectedIndex: number) => {
    if (selectedIndex === 0) {
      return 0;
    }

    return oldIndex === selectedIndex ? selectedIndex - 1 : selectedIndex;
  };

  avatarClick = (index: number) => {
    if (this.props.onChipSelectionUpdate) {
      this.props.onChipSelectionUpdate(index);
    }
  };

  onChipClick = (item: IItem) => () => {
    if (this.props.onClick) {
      this.props.onClick(item);
    }
  };

  previewGenerator = (data: IPreviewData) => {
    const { style, item } = data;
    return (
      <div className="item-list__item" style={{ ...style, width: item.width }}>
        <EDDraggableChipView
          isDragging={false}
          name={item.id}
          showTooltip={false}
          onDelete={this.props.canDelete ? () => {} : undefined}
          chipSelection={this.props.chipSelection}
          dragHandle={this.props.dragHandle}
          avatarHighLighted={false}
          alternateAvatar={item.alternateAvatar}
          clickDisabled={this.props.clickDisabled}
        />
      </div>
    );
  };

  render() {
    return (
      <DndProvider
        backend={this.props.isTouchDevice ? TouchBackend : HTML5Backend}
      >
        {this.props.items.map((item, index) => {
          const key = item.id != null ? item.id : item.name;
          if (!this.props.showHidden && item.hide) {
            return null;
          }
          return (
            <EDTooltip
              key={'chip-' + key}
              title={this.props.disabledReason}
              enableClickOnTouch={this.props.clickDisabled}
            >
              <EDDraggableChip
                deleteIcon={item.deleteIcon}
                showHidden={this.props.showHidden}
                hidden={item.hide}
                disableRipple
                name={item.name}
                id={item.id ?? item.name}
                index={index}
                onDelete={
                  this.props.canDelete ? this.onDelete(item) : undefined
                }
                moveItem={this.moveItem}
                onClick={
                  !this.props.clickDisabled ? this.onChipClick(item) : undefined
                }
                clickDisabled={this.props.clickDisabled}
                showTooltip={!this.props.disabledReason}
                onAvatarClick={
                  this.props.chipSelection
                    ? () => this.avatarClick(index)
                    : undefined
                }
                chipSelection={this.props.chipSelection}
                dragHandle={this.props.dragHandle}
                avatarHighLighted={item.avatarHighLighted}
                avatarTooltip={item.avatarTooltip}
                alternateAvatar={item.alternateAvatar}
                labelIcon={item.labelIcon}
                hoverAvatar={item.hoverAvatar}
              />
            </EDTooltip>
          );
        })}
        {this.props.isTouchDevice && (
          <Preview generator={this.previewGenerator} />
        )}
      </DndProvider>
    );
  }
}

export const EDDraggableChipList: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isTouchDevice = useIsTouchDevice();
  return <EDDraggableChipListBase {...props} isTouchDevice={isTouchDevice} />;
};

export default EDDraggableChipList;
